import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import Loader from '../components/Loader';
import MapEditor from '../components/Map/MapEditor';
import { setXTiles, setYTiles } from '../reducers/gridEdit/gridReducerActions';
import { Map } from '../reducers/map/mapReducer';
import {
    addPing,
    removePing,
    resetMap,
    setMapBackground,
    setMapId,
    setMapValues,
} from '../reducers/map/mapReducerActions';
import { preloadImages } from '../utils/images';
import Client from '../utils/network';
import SpellEffectEditor from '../components/Spells/SpellEffectEditor';
import MeasureEditor from '../components/Map/MeasureEditor';
import ZoomEditor from '../components/Map/ZoomEditor';
import ToggleHotbar from '../components/Map/ToggleHotbar';
import { useSelector } from 'react-redux';
import { Rootstate } from '../store';
import TurnOrderWidget from '../components/Map/TurnOrder/TurnOrderWidget';
import { ObjectId } from 'bson';

const SpectateMap = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [exists, setExists] = useState(true);
    const dispatch = useDispatch();
    const eventSourceRef = useRef<EventSource>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { guildId, channelId } = useSelector((state: Rootstate) => ({
        channelId: state.auth.channelId,
        guildId: state.auth.guildId,
    }), shallowEqual);

    const spectateMap = () => {
        setIsLoading(true);
        setExists(true);
        if(!guildId || !channelId) {
            return;
        }
        Client.request(`/api/map/hostmap?guildId=${guildId}&channelId=${channelId}`).then(async (res) => {
            dispatch(resetMap());
            const body = await res.json() as Map & { mapId: string };
            await preloadImages([`${Client.getBaseUrl()}/api/image/${body.background}`]);
            dispatch(setMapId(body.mapId));
            dispatch(setMapBackground(body.background));
            dispatch(setXTiles(body.backgroundWidth));
            dispatch(setYTiles(body.backgroundHeight));
            dispatch(setMapValues({ ...body }));
            setExists(true);
            setIsLoading(false);
            return;
        }).catch(() => {
            setExists(false);
            setIsLoading(false);
        });
    };

    const createSSERequest = () => {
        return Client.SSERequest(
            `/api/map/listen?guildId=${guildId as string}&channelId=${channelId as string}`,
            (x: string) => {
                const data: Partial<Map>
                    & { mapId?: string, ping?: { x: number, y: number } } = JSON.parse(x) as Partial<Map>;
                dispatch(setMapValues({ ...data }));
                if(data.mapId !== undefined) {
                    spectateMap();
                }
                if(data.ping) {
                    // eslint-disable-next-line no-console
                    const _id = new ObjectId();
                    dispatch(addPing({ ...data.ping, _id }));
                    setTimeout(() => {
                        dispatch(removePing(_id));
                    }, 2000);
                }
            },
        );
    };

    useEffect(() => {
        spectateMap();
        eventSourceRef.current = createSSERequest();

        eventSourceRef.current?.addEventListener('error' , () => {
            eventSourceRef.current?.close();
            eventSourceRef.current = createSSERequest();

        });

        return () => {
            eventSourceRef.current?.close();
        };
    }, [guildId, channelId]);

    // a retry button if the map does not exist
    if (!exists) {
        return (
            <div className='m-auto flex grow justify-center align-center '>
                <div className='flex flex-col'>
                    <p className='text-center'>No map is being hosted</p>
                    <button
                        onClick={spectateMap}
                        className='bg-blue-500 text-white rounded-xs p-2'
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return isLoading
        ? (
            <div className='m-auto flex grow justify-center align-center '>
                <Loader />
            </div>
        ) : (
            <MapEditor canEdit={false} editors={[
                <ZoomEditor key="zoom" />,
                <ToggleHotbar key="togglehotbar">
                    <SpellEffectEditor />
                    <MeasureEditor key="measure" />
                </ToggleHotbar>,
                <TurnOrderWidget key="turn-order" canEdit={false} />,
            ]}/>
        );
};
export default SpectateMap;
